import { Component, Input, OnInit, Output, ViewChild } from "@angular/core";

import { GlobalDataService } from "../../_services/global-data.service";
import { FormGroup } from "@angular/forms";
import {
  Circle,
  County,
  Nest,
} from "../../admin/events-screen/event-page-models/noal.models";
import { SelectMenuOption } from "../../form/select-menu/select-menu.component";
import { LoggerService } from "../../modules/logger/logger.service";
import { SelectOrgansCustomMultiSelectComponent } from "./select-organs-custom-multi-select/select-organs-custom-multi-select.component";
import { SelectMenuAppearance } from "../../form/models/select-menu-apearance";

interface Organ {
  id: number;
  name: string;
  type: "circle" | "county" | "nest";
  parentCircle?: number;
  parentCounty?: number;
}

@Component({
  selector: "select-organs",
  templateUrl: "select-organs.component.html",
  styleUrls: ["select-organs.component.scss"],
})

// קן יא באר שבע
// http://localhost:4200/#/development/stories/organ-selection
export class SelectOrgansComponent implements OnInit {
  @Input("length") public length: number;
  @Input("appearance") public appearance: SelectMenuAppearance;
  @ViewChild("selectOrgansMultiRef")
  public selectOrgansMultiRef: SelectOrgansCustomMultiSelectComponent;

  public allAvailableOrgans: Organ[] = [];
  public allSelectedOrgans: Organ[] = [];
  public allShownOrgansInMenu: Organ[] = [];
  public organMenuOptions: SelectMenuOption[] = [];
  public searchTerm = "";

  constructor(
    private globalDataService: GlobalDataService,
    private logger: LoggerService
  ) {}

  public async initData() {
    try {
      const result = await this.globalDataService.getOrganData();
      if (result.success && result.data) {
        const { circles, counties, nests } = result.data;
        this.allAvailableOrgans = this.convertToOrgans(
          circles,
          counties,
          nests
        );
        this.updateDropdownDisplayedOrgans();
      } else if (!result.success) {
        this.logger.error(
          "Failed to fetch organ data:",
          (result as { success: false; error?: any }).error
        );
      }
    } catch (error) {
      this.logger.error("Error fetching organ data:", error);
    }
  }

  public ngOnInit() {
    this.initData().then();
  }

  public updateDropdownDisplayedOrgans() {
    const allOrgansCloned = this.allAvailableOrgans.map((o) => ({
      ...o,
    }));
    this.allSelectedOrgans = this.removeOrgansContainedByOtherOrgans(
      this.allSelectedOrgans,
      this.allSelectedOrgans,
      allOrgansCloned
    );

    const allOrgansToShowAfterSomeSelected =
      this.removeOrgansContainedByOtherOrgans(
        this.allSelectedOrgans,
        allOrgansCloned,
        allOrgansCloned
      );

    const filteredOrgansBySearch = this.getFilteredOrgans(
      allOrgansToShowAfterSomeSelected,
      this.searchTerm
    );
    const filteredOrgansBySearchWithoutSelected = filteredOrgansBySearch.filter(
      (o) => !this.allSelectedOrgans.some((selected) => selected.id === o.id)
    );

    this.allShownOrgansInMenu = [
      ...this.allSelectedOrgans,
      ...filteredOrgansBySearchWithoutSelected,
    ];
    this.organMenuOptions = this.allShownOrgansInMenu.map(
      this.formatOrganOption
    );

    this.forceSetSelectedOptions(
      this.allSelectedOrgans.map(this.formatOrganOption)
    );
  }

  public onOrgansSelected(selectedOptions: SelectMenuOption[]) {
    this.allSelectedOrgans = selectedOptions
      .map((opt) => opt.customValue as Organ)
      .filter((o) => o);
    this.updateDropdownDisplayedOrgans();
  }

  public forceSetSelectedOptions(selectOrgansMultiRef: SelectMenuOption[]) {
    this.selectOrgansMultiRef.setSelectedOptions(selectOrgansMultiRef);
  }

  public getSelectedOrgans(): Organ[] {
    return this.allSelectedOrgans.map((organ) => ({
      ...organ,
    }));
  }

  private removeOrgansContainedByOtherOrgans(
    selectedOrgans: Organ[],
    organGroupToFilter: Organ[],
    allOrgans: Organ[]
  ): Organ[] {
    const selectedCircles = selectedOrgans.filter(
      (item) => item.type === "circle"
    );
    const selectedFromCircles = allOrgans
      .filter((item) => item.type === "county")
      .filter((county) =>
        selectedCircles.some((circle) => circle.id === county.parentCircle)
      );
    const selectedCounties = [
      ...selectedOrgans.filter((item) => item.type === "county"),
      ...selectedFromCircles,
    ];
    return organGroupToFilter.filter((organ) => {
      if (organ.type === "county") {
        return !selectedCircles.some(
          (circle) => circle.id === organ.parentCircle
        );
      } else if (organ.type === "nest") {
        return (
          !selectedCircles.some((circle) => circle.id === organ.parentCircle) &&
          !selectedCounties.some((county) => county.id === organ.parentCounty)
        );
      }
      return true;
    });
  }

  private getFilteredOrgans(organs: Organ[], searchTerm: string): Organ[] {
    if (!searchTerm) {
      return organs;
    }
    return organs.filter((organ) =>
      organ.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  private convertToOrgans(
    circles: Circle[],
    counties: County[],
    nests: Nest[]
  ): Organ[] {
    const organs: Organ[] = [];
    circles.forEach((circle) => {
      if (!circle.has_add_permission) {
        return;
      }
      organs.push({
        id: circle.circleId,
        name: circle.name,
        type: "circle",
      });
    });
    counties.forEach((county) => {
      if (!county.has_add_permission) {
        return;
      }
      organs.push({
        id: county.countyId,
        name: county.name,
        type: "county",
        parentCircle: county.parent,
      });
    });
    nests.forEach((nest) => {
      if (!nest.has_add_permission) {
        return;
      }
      organs.push({
        id: nest.nestId,
        name: nest.name,
        type: "nest",
        parentCounty: nest.parent,
      });
    });
    return organs;
  }

  private formatOrganOption(organ: Organ): SelectMenuOption {
    return {
      key: organ.id.toString(),
      text: `${organ.name}`,
      icon: "",
      customValue: organ,
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/common/select-organs/select-organs.component.ts